/**
 * Badges Collection Page
 * Shows all badges a player has earned and badges they can still unlock
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, Lock, Star, Flame, Target, Book, Puzzle, Brain, Sparkles } from 'lucide-react';
import { useGame } from '../context/GameContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_INFO = {
  games: { name: 'Game Badges', icon: Trophy, color: 'from-blue-500 to-cyan-500' },
  streaks: { name: 'Streak Badges', icon: Flame, color: 'from-orange-500 to-red-500' },
  points: { name: 'Point Badges', icon: Star, color: 'from-yellow-500 to-amber-500' },
  achievement: { name: 'Achievement Badges', icon: Target, color: 'from-purple-500 to-pink-500' },
  explorer: { name: 'Explorer Badges', icon: Sparkles, color: 'from-green-500 to-teal-500' },
  special: { name: 'Special Badges', icon: Brain, color: 'from-indigo-500 to-purple-500' }
};

const BadgesPage = () => {
  const navigate = useNavigate();
  const { playerId } = useGame();
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stats, setStats] = useState({ total: 0, earned: 0 });

  useEffect(() => {
    fetchBadges();
  }, [playerId]);

  const fetchBadges = async () => {
    try {
      // Fetch all badge definitions
      const defsResponse = await axios.get(`${API}/badges/definitions`);
      const allBadgeDefs = defsResponse.data.badges;
      setAllBadges(allBadgeDefs);

      if (playerId) {
        // Fetch player's badges
        const playerResponse = await axios.get(`${API}/badges/player/${playerId}`);
        const earnedBadges = playerResponse.data.badges_earned || [];
        setBadges(earnedBadges);
        setProgress(playerResponse.data.progress || {});
        setStats({
          total: allBadgeDefs.length,
          earned: earnedBadges.length
        });
      } else {
        setStats({
          total: allBadgeDefs.length,
          earned: 0
        });
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const earnedBadgeIds = badges.map(b => b.id);

  const getFilteredBadges = () => {
    if (selectedCategory === 'all') {
      return allBadges;
    }
    if (selectedCategory === 'earned') {
      return allBadges.filter(b => earnedBadgeIds.includes(b.id));
    }
    if (selectedCategory === 'locked') {
      return allBadges.filter(b => !earnedBadgeIds.includes(b.id));
    }
    return allBadges.filter(b => b.category === selectedCategory);
  };

  const filteredBadges = getFilteredBadges();

  // Group badges by category
  const badgesByCategory = {};
  filteredBadges.forEach(badge => {
    if (!badgesByCategory[badge.category]) {
      badgesByCategory[badge.category] = [];
    }
    badgesByCategory[badge.category].push(badge);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card className="p-8 bg-white/95 text-center" style={{ borderRadius: '20px' }}>
          <div className="animate-spin text-6xl mb-4">🏆</div>
          <p className="text-xl font-bold text-gray-700">Loading Badges...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>

        {/* Title Card */}
        <Card className="p-6 md:p-8 bg-white/95 backdrop-blur-sm shadow-2xl mb-6" style={{ borderRadius: '24px' }}>
          <div className="text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Badge Collection</h1>
            <p className="text-gray-600 mb-4">Collect badges by playing games and reaching milestones!</p>
            
            {/* Progress Bar */}
            <div className="max-w-md mx-auto">
              <div className="flex justify-between text-sm font-medium text-gray-600 mb-2">
                <span>{stats.earned} Badges Earned</span>
                <span>{stats.total} Total</span>
              </div>
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                  style={{ width: `${(stats.earned / stats.total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Progress Stats */}
        <Card className="p-4 md:p-6 bg-white/95 backdrop-blur-sm shadow-xl mb-6" style={{ borderRadius: '20px' }}>
          <h3 className="text-lg font-bold text-gray-800 mb-4">Your Progress</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <div className="text-2xl mb-1">🎯</div>
              <div className="text-2xl font-bold text-blue-600">{progress.quizzes_completed || 0}</div>
              <div className="text-xs text-gray-500">Quizzes</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-xl">
              <div className="text-2xl mb-1">🧠</div>
              <div className="text-2xl font-bold text-purple-600">{progress.memory_games_completed || 0}</div>
              <div className="text-xs text-gray-500">Memory Games</div>
            </div>
            <div className="text-center p-3 bg-pink-50 rounded-xl">
              <div className="text-2xl mb-1">📖</div>
              <div className="text-2xl font-bold text-pink-600">{progress.stories_completed || 0}</div>
              <div className="text-xs text-gray-500">Stories</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-xl">
              <div className="text-2xl mb-1">🔥</div>
              <div className="text-2xl font-bold text-orange-600">{progress.current_streak || 0}</div>
              <div className="text-xs text-gray-500">Day Streak</div>
            </div>
          </div>
        </Card>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {[
            { key: 'all', label: 'All Badges' },
            { key: 'earned', label: `Earned (${stats.earned})` },
            { key: 'locked', label: `Locked (${stats.total - stats.earned})` }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setSelectedCategory(tab.key)}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                selectedCategory === tab.key
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'bg-white/30 text-white hover:bg-white/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Badges Grid by Category */}
        {Object.entries(badgesByCategory).map(([category, categoryBadges]) => {
          const categoryInfo = CATEGORY_INFO[category] || { name: category, icon: Trophy, color: 'from-gray-500 to-gray-600' };
          const CategoryIcon = categoryInfo.icon;

          return (
            <Card key={category} className="p-6 bg-white/95 backdrop-blur-sm shadow-xl mb-6" style={{ borderRadius: '20px' }}>
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-xl bg-gradient-to-r ${categoryInfo.color}`}>
                  <CategoryIcon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">{categoryInfo.name}</h2>
                <span className="text-sm text-gray-500">
                  ({categoryBadges.filter(b => earnedBadgeIds.includes(b.id)).length}/{categoryBadges.length})
                </span>
              </div>

              {/* Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {categoryBadges.map(badge => {
                  const isEarned = earnedBadgeIds.includes(badge.id);
                  const earnedBadge = badges.find(b => b.id === badge.id);

                  return (
                    <div
                      key={badge.id}
                      className={`relative p-4 rounded-2xl text-center transition-all duration-300 ${
                        isEarned
                          ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 shadow-lg hover:scale-105'
                          : 'bg-gray-100 border-2 border-gray-200 opacity-60'
                      }`}
                    >
                      {/* Lock icon for unearned badges */}
                      {!isEarned && (
                        <div className="absolute top-2 right-2">
                          <Lock className="w-4 h-4 text-gray-400" />
                        </div>
                      )}

                      {/* Badge emoji */}
                      <div className={`text-4xl md:text-5xl mb-2 ${!isEarned ? 'grayscale' : ''}`}>
                        {badge.emoji}
                      </div>

                      {/* Badge name */}
                      <h3 className={`font-bold text-sm ${isEarned ? 'text-gray-800' : 'text-gray-500'}`}>
                        {badge.name}
                      </h3>

                      {/* Badge description */}
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {badge.description}
                      </p>

                      {/* Points */}
                      <div className={`mt-2 text-xs font-bold ${isEarned ? 'text-yellow-600' : 'text-gray-400'}`}>
                        +{badge.points} pts
                      </div>

                      {/* Earned date */}
                      {isEarned && earnedBadge?.earned_at && (
                        <div className="mt-1 text-xs text-green-600">
                          ✓ Earned
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}

        {/* Empty state */}
        {filteredBadges.length === 0 && (
          <Card className="p-8 bg-white/95 text-center" style={{ borderRadius: '20px' }}>
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No badges yet!</h3>
            <p className="text-gray-500 mb-4">Start playing games to earn your first badge!</p>
            <Button
              onClick={() => navigate('/game-format')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            >
              Play Now!
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BadgesPage;
